/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState, useRef } from 'react';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material components
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

// Images
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";

import LeasesService from "services/leases-service";
import PaymentsService from "services/payments-service";
import LeaseInvoiceService from "services/lease-invoice-service";

export default function PropertiesData() {
  const [propertiesData, setPropertiesData] = useState([])
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [innerLoading, setInnerLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [editedRow, setEditedRow] = useState(null);
  const [sortField, setSortField] = useState("invoice_number"); // default sort field
  const [sortDirection, setSortDirection] = useState("desc"); // default sort direction
  const totalRows = useRef(0);

  const getInvoiceList = async (pageIndex = '', pageSize = '', filter = "", sortField="updated_at", sortDirection="desc", propertyId) => {
    try {
      setInnerLoading(true);
      setButtonDisabled(true)
      const data = await LeaseInvoiceService.getInvoiceList({
        limit: pageSize,
        page: pageIndex,
        sortField: sortField,
        sortDirection: sortDirection,
        filter: filter,
        propertyId: propertyId,
        whereField: propertyId === "all" ? "" : 'property_id',
        whereValue: propertyId === "all" ? "" : propertyId
      }, propertyId);
      setTotalPages(data.meta.last_page);
      totalRows.current = data.meta.total;
      // console.log(data.data);
      const invoiceList = data.data.map(item => {
        const { id, property_id, invoice_number, invoice_prop_number, lease, period, remarks, summary, status } = item;
        const { invoice_amount, amount_paid, amount_due } = summary || {};
        const { status_color, status_icon, status_text } = summary?.status || {};

        return {
          id,
          property_id,
          invoice_number,
          invoice_prop_number,
          tenant_code: lease?.lease_code,
          period_name: period?.name,
          invoice_amount: invoice_amount || 0,
          amount_paid: amount_paid || 0,
          amount_due: amount_due || 0,
          status_color: status_color || "text-secondary",
          status_icon: status_icon || "pending_actions",
          status_text: status_text || "Pending",
          remarks: remarks || "",
        };
      });
      setPropertiesData(invoiceList);
    } catch (error) {
      console.error('Error get properties list:', error);
    } finally {
      setButtonDisabled(false);
      setInnerLoading(false);
    }
  }

  const getPropertyLeasesList = async (pageIndex = '', pageSize = '', filter = "", sortField="updated_at", sortDirection="desc", propertyId) => {
    try {
      setInnerLoading(true);
      setButtonDisabled(true)
      const data = await LeaseInvoiceService.getPropertyLeasesList({
        limit: pageSize,
        page: pageIndex,
        sortField: sortField,
        sortDirection: sortDirection,
        filter: filter
      }, propertyId);
      setTotalPages(data.meta.last_page);
      totalRows.current = data.meta.total;

      const propertiesList = data.data.map(({ id, lease_code, tenants, unit_names, rent_amount, lease_type: { lease_type_display_name }, due_on, billed_on, tenant_names, status: { status_color, status_text, status_icon }, area_size, remarks  }) => {
        let price_per_sqft = "N/A";
        if (parseInt(area_size) > 0) {
          price_per_sqft = (rent_amount / parseInt(area_size)).toFixed(2);
        }

        return {
          id,
          lease_code,
          unit: unit_names,
          rent_amount,
          lease_type: lease_type_display_name,
          due_on,
          last_billing: billed_on,
          tenant_names,
          tenants,
          area: area_size,
          price_per_sqft: price_per_sqft,
          remarks: remarks,
          status_color,
          status_text,
          status_icon
        }
      });
      setPropertiesData(propertiesList);
    } catch (error) {
      console.error('Error get properties list:', error);
    } finally {
      setButtonDisabled(false);
      setInnerLoading(false);
    }
  }

  const handleEditPayment = (item) => {
    setOpen(true);
    setIsShowDialog(true);
    setEditedRow(item);
  }

  const handleEditLease = (item) => {
    setOpen(true);
    setIsShowDialog(true);
    setEditedRow(item);
  }

  const handleDuplicateLeaseInvoice = async (item) => {
    const confirm = window.confirm("Are you sure you want to duplicate this lease invoice?");
    if (!confirm) return;
      
    try {
      setInnerLoading(true);
      setButtonDisabled(true);
      const response = await LeaseInvoiceService.duplicateLeaseInvoice({ invoice_id: item.id });
      if (!response.error) {
        alert("Lease invoice duplicated successfully.");
        // Reload page 
        getInvoiceList(1, 25, "", sortField, sortDirection, item.property_id);
      } else {
        alert("Failed to duplicate lease invoice.");
      }
    } catch (error) {
      console.error("Error duplicating lease invoice:", error);
      // alert("An error occurred while duplicating the lease invoice.");
    } finally {
      setInnerLoading(false);
      setButtonDisabled(false);
    }
  }

  useEffect(() => {
    const data = propertiesData.map((item, index) => ({
      ...item,
      no: index,
      invoice_prop_number: (
      <MDTypography component="a" target="_blank" href={`https://mgt.yeelim.org/#/invoices/${item.id}`} variant="button" sx={{ color: "info.main" }} fontWeight="medium">
        {item.invoice_prop_number}
      </MDTypography>
      ),
      period_name: (() => {
        const [start_date, end_date] = item.remarks?.split("|") || [];
        // console.log(item);
        return (
          <MDTypography variant="button" fontWeight="medium">
            {start_date && end_date ? `${start_date} - ${end_date}` : item.period_name}
          </MDTypography>
        );
      })(),
      status: (
      <MDBox ml={-1} className={`${item.status_color}`} display="flex" alignItems="center">
        <Icon fontSize="14px">{item.status_icon}</Icon>
        <MDTypography component="span" fontSize="14px" sx={{ marginLeft: "5px", fontWeight: "400" }} className={`${item.status_color}`}>
        {item.status_text}
        </MDTypography>
      </MDBox>
      ),
      action: (() => {
        const [start_date, end_date] = item.remarks?.split("|") || [];
        // console.log(item);
        if( start_date && end_date ) {
          return (
            <>
              <MDTypography component="a" href="#" color="text">
                <IconButton size="small" onClick={() => handleEditLease(item)} ><Icon>edit</Icon></IconButton>
              </MDTypography>
              <MDTypography component="a" href="#" color="text">
                <IconButton size="small" onClick={() => handleDuplicateLeaseInvoice(item)} ><Icon>copy</Icon></IconButton>
              </MDTypography>
            </>
          );
        } else {
          return (
            <MDTypography component="a" href="#" color="text">
              <IconButton size="small" onClick={() => handleEditLease(item)} ><Icon>edit</Icon></IconButton>
            </MDTypography>
          );
        }
      })(),
    }));
    setRows(data);
  }, [propertiesData]);

  return {
    columns: [
      { Header: "Invoice Number", accessor: "invoice_prop_number", align: "left" },
      { Header: "Tenant Code", accessor: "tenant_code", align: "left" },
      { Header: "Period", accessor: "period_name", align: "left", contentWidth: "125px"},
      { Header: "Amount", accessor: "invoice_amount", align: "left" },
      { Header: "Paid", accessor: "amount_paid", align: "left" },
      { Header: "Balance", accessor: "amount_due", align: "left" },
      { Header: "Status", accessor: "status", align: "left" },
      { Header: "Action", accessor: "action", enableSorting: false, align: "left" }
    ],
    rows: rows,
    totalPages: totalPages,
    getPropertyLeasesList: getPropertyLeasesList,
    getInvoiceList: getInvoiceList,
    innerLoading: innerLoading,
    buttonDisabled: buttonDisabled,
    setButtonDisabled: setButtonDisabled,
    open: open,
    setOpen: setOpen,
    isShowDialog: isShowDialog,
    sortField: sortField,
    setSortField: setSortField,
    sortDirection: sortDirection,
    setSortDirection: setSortDirection,
    editedRow: editedRow,
    totalRows: totalRows
  };
}
